<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'LockScreen',
})
</script>
<template>
  <section 
  ref="lockScreenRef"  
  class="LockScreen" v-if="lockScreen" :class="afterUnlock" @keydown="handleKeyPress"  @click="handleAnyClick"   tabindex="0">
    <div v-if="lockImg" class="bg" :class="isClickedUnlockBtn ? 'unlockClicked' : ''" 
      :style="{ backgroundImage: `url(${lockImg})` }"></div>
    <div class="content">
      <!-- 点击解锁后出现输入框 -->
      <div class="iptPassword" v-if="isClickedUnlockBtn">
        <img 
          :src="userinfo?.avatar || '/user.png'" 
          class="avatar" 
          alt="用户头像"
          @error="e => e.target.src = '/user.png'"
        />
        <div class="username">{{ userinfo?.username || '用户' }}</div>
        <!-- 添加ref属性用于获取焦点 -->
        <a-input-search 
          ref="passwordInputRef"
          class="passwordInput"
          v-model:value="password" 
          placeholder="密码" 
          style="width: 340px" 
          type="password" 
          @search="onUnlockScreen"
        >
          <template #enterButton>
            <a-button type="link">
              <template #icon>
                <div class="arrow-right-icon">
                  <ArrowRightOutlined />
                </div>
              </template>
            </a-button>
          </template>
        </a-input-search>
        <div class="wrongPwd" v-if="isWrongPwd">密码不正确</div>
      </div>
      <!-- 未点击解锁可修改背景图片 -->
      <a-upload :before-upload="onChangeBg" :showUploadList="false" v-else>
        <div class="uploadTxt">
          <SearchOutlined class="changeBg" />
          <div class="txt">点击更换背景图片</div>
        </div>
      </a-upload>
      <!-- 左下角时间 -->
      <div class="timeZone">
        <div class="currentTime">{{ currentTime }}</div>
        <div class="dateTime">{{ dateTime }}</div>
      </div>
      <!-- 右下角三个按钮 -->
      <div class="deviceStatus">
        <!-- 是否联网 -->
        <span class="onlineStatus">
          <img src="/icons/onLine.ico" v-show="isOnline" />
          <img src="/icons/offLine.ico" v-show="!isOnline" />
        </span>
        <!-- 是否充电 -->
        <SvgIcon :name="charging ? 'batteryCharge' : 'battery'" color="#fff" width="33px" height="33px" class="charge" />
        <!-- 解锁按钮 -->
        <a-tooltip>
        <template #title>{{ isClickedUnlockBtn ? '返回' : '点击解锁' }}</template>
        <span class="unlock-icon-container">
          <LockOutlined 
            class="unlock" 
            @click.stop="onClickLockBtn" 
            v-if="isClickedUnlockBtn" 
            data-test="lock-icon"
          />
          <UnlockOutlined 
            class="unlock" 
            @click.stop="onClickUnlockBtn" 
            v-else 
            data-test="unlock-icon"
          />
        </span>
      </a-tooltip>
      </div>
    </div>
  </section>
</template>
<script lang="ts" setup name="LockScreen">
import { onMounted, ref, nextTick } from 'vue' // 引入nextTick用于DOM更新后操作
import { systemConfig } from '@/store/reactiveState'
import useSiteSettings from '@/store/useSiteSettings'
import { SET_LOCK_SCREEN } from '@/store/mutation-types'
import indexdb from '@/utils/indexDB'
import dayjs from 'dayjs';
import weekday from "dayjs/plugin/weekday";
import "dayjs/locale/zh-cn";
import { getWeek } from '@/utils/util'
import { useBattery, useNetwork } from '@vueuse/core'
import SvgIcon from '@/components/SvgIcon/index.vue'
import {
  SearchOutlined,
  UnlockOutlined,
  LockOutlined,
  ArrowRightOutlined,
  UserOutlined
} from '@ant-design/icons-vue'
import { USER_INFO } from '@/store/mutation-types'
import ls from '@/utils/Storage'
import * as api from './service'

dayjs.extend(weekday);
const userinfo = ls.get(USER_INFO)
const initBg = '/lock.png'

const isClickedUnlockBtn = ref(false)
const { lockScreen } = useSiteSettings()

// 创建ref用于获取输入框组件
const passwordInputRef = ref(null)

// 点击解锁按钮 - 增强调试和焦点处理
const onClickUnlockBtn = () => {
  console.log('解锁按钮被点击，当前状态:', isClickedUnlockBtn.value); // 增强调试信息
  isClickedUnlockBtn.value = true
  
  // 确保DOM更新后再聚焦
  nextTick(() => {
    if (passwordInputRef.value) {
      // 针对ant-design组件的特殊处理
      const inputElement = passwordInputRef.value.input || passwordInputRef.value.$el.querySelector('input');
      if (inputElement) {
        inputElement.focus();
        console.log('密码输入框已获得焦点');
      } else {
        console.warn('未找到密码输入框元素');
      }
    } else {
      console.warn('passwordInputRef未初始化');
    }
  })
}

// 点击返回按钮 - 增强调试
const onClickLockBtn = () => {
  console.log('返回按钮被点击，当前状态:', isClickedUnlockBtn.value); // 增强调试信息
  isClickedUnlockBtn.value = false
  password.value = '' // 重置密码
  
  // 回到锁屏状态时重新获取焦点
  nextTick(() => {
    if (lockScreenRef.value) {
      lockScreenRef.value.focus();
      console.log('锁屏区域已获得焦点');
    }
  })
}
const handleKeyPress = (e: KeyboardEvent) => {
  if (!isClickedUnlockBtn.value) {
    e.preventDefault()
    e.stopPropagation()
    onClickUnlockBtn()
  } else {
    // 已解锁状态下按ESC键返回
    if (e.key === 'Escape') {
      e.preventDefault()
      onClickLockBtn()
    }
  }
}// 处理任意位置点击事件 - 完善判断逻辑
const handleAnyClick = (e: MouseEvent) => {

  // 1. 检查点击目标是否是解锁/返回按钮
  const isClickOnUnlockBtn = e.target instanceof HTMLElement && 
                            (e.target.closest('.unlock') || 
                            e.target.closest('.unlock-icon-wrapper'));
                            
  // 2. 检查点击目标是否是背景图片上传区域（更严格的判断）
  const isClickOnBgUpload = e.target instanceof HTMLElement && 
                           (e.target.closest('.bg-upload-container') || 
                            e.target.closest('.bg-upload-area') ||
                            e.target.closest('.uploadTxt') ||
                            e.target.closest('.changeBg') ||
                            e.target.closest('.txt')||
                            e.target.closest('.bg-upload-container')
                          );
                           
  // 如果点击了按钮或背景上传区域，直接返回不处理
  if (isClickOnUnlockBtn || isClickOnBgUpload) {
    // 阻止事件冒泡到父元素
    e.stopPropagation();
    return;
  }
  
  // 未解锁状态：点击任意位置触发解锁
  if (!isClickedUnlockBtn.value) {
    e.stopPropagation();
    onClickUnlockBtn();
  } 
  // 已解锁状态：点击任意位置返回锁屏
  else {
    // 排除点击密码输入框区域时的情况
    const isClickOnInput = e.target instanceof HTMLElement && 
                          (e.target.closest('.ant-input') || 
                           e.target.closest('.ant-btn') ||
                           e.target.closest('.iptPassword'));
                           
    if (!isClickOnInput) {
      onClickLockBtn();
    }
  }
}
// 关键修复：添加lockScreenRef的定义
const lockScreenRef = ref(null)  // 新增这一行，定义ref
// 获取锁屏壁纸
const lockImg = ref(null)
onMounted(async () => {
  // 1. 先加载背景图
  setTimeout(async () => {
    const bg = await indexdb.get('lockImg')
    lockImg.value = bg?.value || initBg
  }, 100)
  

 })

// 获取左下角日期时间
const getDateTime = () => {
  const week = dayjs().weekday()
  const formatedWeek = getWeek(week, false)
  const day = dayjs().date()
  const month = dayjs().month() + 1
  return month + '月' + day + '日' + ', ' + formatedWeek
}
const dateTime = ref(getDateTime())
const currentTime = ref(dayjs().format('HH:mm'))
setInterval(() => {
  currentTime.value = dayjs().format('HH:mm')
  dateTime.value = getDateTime()
}, 1000)

// 获取电池状态
const { charging } = useBattery()
// 获取网络状态
const { isOnline } = useNetwork()

const onChangeBg = (file) => {
  if (file.type.indexOf('image') < 0) {
    alert('只能传图片!')
  }
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onload = function () {
    indexdb.set('lockImg', this.result)
    lockImg.value = this.result
  }
  return false
}

const password = ref('')
const isWrongPwd = ref(false)
const afterUnlock = ref('')

const onUnlockScreen = () => {
  // 检查用户信息是否存在
  if (!userinfo?.username) {
    isWrongPwd.value = true
    return
  }
  // api.userLogin({ code: userinfo.username, pass: password.value }).then((res: any) => {
  api.userLogin({ username: userinfo.username, password: password.value }).then((res: any) => {
    if (!password.value) {
      isWrongPwd.value = true
      return false
    } else {
      isWrongPwd.value = false
    }
    if (res) {
      if (res.unlocked) {
        afterUnlock.value = 'afterUnlock'
        setTimeout(() => {
          systemConfig.commit('SET_LOCK_SCREEN', false)
          afterUnlock.value = ''
          isWrongPwd.value = false
          isClickedUnlockBtn.value = false
          password.value = ''
        }, 300)
      } else {
        isWrongPwd.value = true
      }
    } else {
      isWrongPwd.value = true
    }
  }).catch(() => {
    // 捕获请求异常
    isWrongPwd.value = true
  })
}
</script>
<style lang="less" scoped>
.afterUnlock {
  opacity: 0 !important;
  transition: opacity 0.3s;
}

.LockScreen {
  background-color: rgba(0, 0, 0, 0.6);
  opacity: 1;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 106;
  overflow: hidden;
  ::v-deep .ant-input-group-addon {
    background-color: transparent;
    border: none; /* 可能需要清除边框 */
  }
  .bg {
    width: 100%;
    height: 100%; /* 确保容器填满屏幕 */
    position: absolute;
    z-index: 1;
    background-color: grey; /* 当图片加载慢或失败时的背景色 */

    /* 核心修改在这里 */
    background-size: cover; /* 改为 cover */
    background-repeat: no-repeat;

    /* 可选优化：让图片居中显示，裁剪时会裁掉两边 */
    background-position: center center;
  }

  .unlockClicked {
    transform: scale(1.1);
    filter: blur(10px);
    transition: all 0.1s;
  }

  .content {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 2;

    .iptPassword {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      
      .avatar {
        width: 130px;
        height: 130px;
        background-color: #e8eceb;
        border-radius: 50%;
        font-size: 80px;
        line-height: 130px;
        padding: 5px;
      }

      .username {
        color: #fff;
        font-size: 40px;
        text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
        margin: 10px auto 15px auto;
      }

      .wrongPwd {
        color: #f88070;
        font-size: 16px;
        text-align: left;
        margin: 5px 0;
        width: 100%;
        padding-left:30px ;
        
      }
      
      :deep(.ant-input::placeholder) {
        color: #aaa;
        
      }

      ::v-deep(.ant-input) {
        caret-color: black;
        border-radius: 0;
        border-color: grey; /* 正常 */
        background-color: #555;
        color:rgb(255, 255, 255);        
        &:hover {
          border-color: white; /* 悬停 */
          background-color: #fff;
          box-shadow: none;
          color:black;        
        }

        &:focus {
          outline: none;
          border-color: grey; /* 聚焦 */
          box-shadow: none;
          background-color: #fff;
          color:black;        
        }

        /* 复合状态：当聚焦且悬停时，显示白色边框 */
        &:focus:hover {
          border-color: white;
          background-color: #fff;
          color:black;        
        }
      }
  


      .ant-btn.ant-btn-link {
        width: 30px;
        height: 29px;
        background-color: grey;
        border: 1px solid #fff;
        left: -30.5px;
        z-index: 2;
        &:hover {
          color: #fff;
          border-color: #fff;
        }
        &:focus {
          outline: none;
          border-color: grey; /* 聚焦 */
          box-shadow: none;
        }
        .arrow-right-icon {
          color: #fff;
          &:hover {
            color: rgba(0, 0, 0, 0.6);
          }
          
        }
      }
    }

    .uploadTxt {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      position: absolute;
      left: 45%;
      top: 50%;
      color: #fff;

      &:hover {
        color: gray;
      }

      .changeBg {
        font-size: 20px;
        cursor: pointer;
        margin-right: 15px;
      }

      .txt {
        cursor: pointer;
      }
    }

    .timeZone {
      position: absolute;
      bottom: 50px;
      left: 20px;
      color: #fff;

      .currentTime {
        font-size: 100px;
        font-weight: 100;
        height: 90px;
        line-height: 90px;
      }

      .dateTime {
        font-size: 60px;
        font-weight: 300;
      }
    }
    

    .deviceStatus {
      position: absolute;
      bottom: 30px;
      right: 50px;

      .onlineStatus {
        margin-right: 20px;
        display: inline-block;

        img {
          width: 25px;
          height: 25px;
        }
      }

      .onlineStatus,
      .charge,
      .unlock {
        vertical-align: middle;
      }

      .unlock {
        font-size: 23px;
        color: #fff;
        margin-left: 20px;
        font-weight: 500;

        &:hover {
          color: gray;
        }
      }
    }
  }
  .passwordInput{

    padding-left: 30px;
  }
}
</style>
